import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Building2, Coins, Banknote, ClipboardCheck } from 'lucide-react';
import LightBackground from '@/components/LightBackground';

const CryptoPolicy = () => {
  const lastUpdated = 'May 22, 2026';

  return (
    <>
      <Helmet>
        <title>Payments Policy &amp; Source of Funds – Aura Dynamics FZCO</title>
        <meta
          name="description"
          content="Internal compliance policy covering acceptance of card payments via Stripe and cryptocurrency payments via Binance Pay Merchant by Aura Dynamics FZCO."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />

        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </header>

        <main className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Payments Policy &amp; Source of Funds
            </h1>
            <p className="text-slate-600">Aura Dynamics FZCO · License #87988 · Last updated: {lastUpdated}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-10 space-y-10">

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">1. Company &amp; Activity</h2>
              </div>
              <p className="text-slate-700 leading-relaxed">
                Aura Dynamics FZCO ("the Company") operates online digital education platforms and AI-based digital
                tools, including the brand <strong>Faceless Aura Club</strong>. The Company sells online
                subscriptions, lifetime memberships, AI credits, digital courses, website and application
                development services, and online consulting services to a global customer base.
              </p>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Banknote className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">2. Accepted Payment Methods</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>The Company accepts customer payments through established third-party payment channels:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>
                    <strong>Stripe</strong> — card payments, Apple Pay, Google Pay, and other fiat payment methods
                    enabled by Stripe, processed by Stripe and its applicable regulated affiliates as identified in the
                    relevant Stripe Services Agreement.
                  </li>
                  <li>
                    <strong>Binance Pay Merchant</strong> — cryptocurrency payments processed through Binance's
                    merchant payments product, settled to the Company's corporate Binance merchant account.
                  </li>
                </ul>
                <p>Both methods are used solely as merchant payment rails for the Company's own products and services.</p>
                <p className="font-medium">Neither payment method, and no activity of the Company, constitutes:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>crypto exchange, brokerage, or OTC services;</li>
                  <li>custody, wallet, or e-money services for third parties;</li>
                  <li>investment management or asset management;</li>
                  <li>trading or market-making services;</li>
                  <li>payment token services provided to third parties;</li>
                  <li>financial, tax, legal, or investment advice of any kind.</li>
                </ul>
                <p>
                  The Company does not issue, promote, convert, safeguard, transfer, or provide payment token services
                  to third parties. Any conversion or settlement is performed by the relevant third-party provider and
                  not by the Company as a service to customers.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Coins className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">3. Stripe Card &amp; Fiat Payments</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Card and fiat payments are processed by <strong>Stripe</strong> under Stripe's applicable terms,
                  compliance framework, and regulatory permissions. Stripe acts as the third-party payment processor
                  and/or acquiring partner, as applicable; the Company does not store full card numbers, CVCs, or other
                  sensitive cardholder data on its own systems.
                </p>
                <p>
                  Stripe settles net funds (gross sale minus Stripe processing fees, refunds, and chargebacks) in the
                  applicable settlement currency to the Company's <strong>Wio Business</strong> bank account on Stripe's
                  standard payout schedule. Refunds, chargebacks, and disputes are handled through Stripe's standard
                  dispute and reversal mechanisms.
                </p>
                <p>
                  Each Stripe payment is linked in the Company's internal records to a unique order ID, customer email,
                  product or service purchased, amount, currency, date, Stripe <em>payment intent</em> / <em>charge</em>
                  {' '}ID, payout reference, and the corresponding invoice or receipt issued by Aura Dynamics FZCO.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Coins className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">4. Binance Pay Crypto Payments</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  The Company accepts cryptocurrency payments exclusively through <strong>Binance Pay Merchant</strong> as
                  an alternative payment method for customer purchases of the Company's own products and services.
                </p>
                <p>
                  Crypto payments are received <strong>only</strong> as consideration for the Company's own products and
                  services, in the same commercial manner as a fiat card or bank transfer. The Company does not custody
                  customer crypto assets and does not operate customer wallets.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <Banknote className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-semibold text-slate-900">5. Settlement &amp; Banking</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Funds received through <strong>Stripe</strong> are settled in fiat (net of Stripe fees) directly to
                  the Company's <strong>Wio Business</strong> bank account on Stripe's standard payout schedule.
                </p>
                <p>
                  Funds received through <strong>Binance Pay Merchant</strong> are settled into the Company's Binance
                  corporate merchant account and may be converted into fiat or an approved settlement currency before
                  being transferred to the Company's <strong>Wio Business</strong> bank account as merchant revenue.
                </p>
                <p>
                  The Company does not operate customer wallets, does not custody customer assets, and does not hold
                  crypto or fiat balances on behalf of customers. Once a customer payment is completed, the payment is
                  treated solely as revenue of the Company for the product or service purchased.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <ClipboardCheck className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">6. Transaction Recordkeeping</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>Every Stripe and Binance Pay checkout creates an internal record containing:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>a unique merchant order ID;</li>
                  <li>customer email address and account reference;</li>
                  <li>the specific product or service purchased;</li>
                  <li>amount, currency, and date/time;</li>
                  <li>payment provider reference — Stripe <em>payment intent</em> / <em>charge</em> ID, or Binance Pay transaction ID / prepay ID;</li>
                  <li>settlement amount, currency, and payout reference;</li>
                  <li>the corresponding invoice or receipt number issued by Aura Dynamics FZCO;</li>
                  <li>service delivery confirmation, such as account activation, content access, AI credit allocation, or deliverable completion.</li>
                </ul>
                <p>
                  These records are retained for a minimum of <strong>5 years</strong> and are reconcilable end-to-end:
                  customer order → Stripe charge or Binance Pay transaction → merchant settlement → bank transfer →
                  issued invoice → delivered service.
                </p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">7. Invoicing</h2>
              </div>
              <p className="text-slate-700 leading-relaxed">
                An invoice or receipt is issued by Aura Dynamics FZCO for every sale processed via Stripe or Binance Pay,
                either on a per-transaction basis or in approved batches, where permitted by the Company's accountant and
                applicable UAE Free Zone invoicing requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Source of Funds Statement</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  All amounts credited to the Company's Wio Business bank account from Stripe payouts or Binance Pay
                  Merchant settlements represent <strong>legitimate revenue from the sale of the Company's own digital
                  products and services</strong> to identifiable customers.
                </p>
                <p>
                  The Company can produce, upon request from its bank, payment provider, accountant, auditor, regulator,
                  or any competent authority, a complete reconciliation showing which customer paid, for which product or
                  service, at what amount, on which date, under which invoice or receipt, and through which Stripe charge,
                  Binance Pay transaction, or merchant payout.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Compliance &amp; Restrictions</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>The Company may refuse, refund, reverse, suspend access, or escalate for compliance review any payment that:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>originates from a sanctioned jurisdiction or sanctioned party;</li>
                  <li>cannot be linked to a legitimate customer order on the platform;</li>
                  <li>appears structured, layered, unusually large, or otherwise suggestive of money laundering, sanctions evasion, fraud, or terrorist financing;</li>
                  <li>violates the Stripe Services Agreement, Binance Pay Merchant terms, applicable law, or the Company's internal compliance standards.</li>
                </ul>
                <p>
                  Suspicious activity is escalated internally and, where applicable, reported to the relevant authority
                  in accordance with applicable UAE AML/CFT obligations.
                </p>
                <p>
                  The UAE Central Bank's <strong>Payment Token Services Regulation</strong> restricts persons from
                  performing regulated payment token services in or directed to the UAE unless licensed or registered.
                  This policy is therefore limited to the Company's acceptance of card, fiat, and crypto payments as
                  consideration for its own goods and services. It does not authorise, and should not be interpreted as
                  authorising, the Company to provide regulated crypto, payment token, exchange, custody, brokerage,
                  e-money, remittance, or payment services to third parties.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Contact</h2>
              <div className="text-slate-700 leading-relaxed space-y-2">
                <p>For compliance, banking, or audit enquiries regarding this policy:</p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:hello@auradynamics.management" className="text-blue-600 hover:text-blue-700 underline">
                    hello@auradynamics.management
                  </a>
                </p>
                <p><strong>Company:</strong> Aura Dynamics FZCO</p>
                <p><strong>License:</strong> #87988</p>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default CryptoPolicy;
